import { showBackofficeFlash } from "packs/init-backoffice-flashes"

const ANIMATION_MS = 280
const UNDO_MS = 10_000

let documentListenersBound = false
let session = null

const liveRoot = () => {
  const root = document.querySelector("[data-registration-review]")
  return root && document.body.contains(root) ? root : null
}

const csrfToken = (root) =>
  root?.dataset.csrfToken ||
  document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")

const parseQueue = (root) => {
  try {
    const raw = JSON.parse(root.dataset.queue || "[]")
    if (!Array.isArray(raw)) return []
    return raw
      .map((item) => ({
        id: Number(item.id),
        card_url: item.card_url || null
      }))
      .filter((item) => item.id)
  } catch (_error) {
    return []
  }
}

const cardUrl = (root, item) => {
  if (item?.card_url) return item.card_url
  const id = typeof item === "object" ? item?.id : item
  return (root.dataset.cardUrlTemplate || "").replace("__ID__", String(id))
}

const remainingLabel = (count) => {
  if (count <= 0) return "0 restante"
  if (count === 1) return "1 restante"
  return `${count} restantes`
}

const postDecision = async ({ root, id, decision, note, participantType }) => {
  const url =
    decision === "approve"
      ? `/back-office/actions-en-attente/${id}/approve`
      : `/back-office/actions-en-attente/${id}/reject`

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-CSRF-Token": csrfToken(root),
      "X-Requested-With": "XMLHttpRequest"
    },
    body: JSON.stringify({ note, participant_type: participantType }),
    credentials: "same-origin"
  })

  const data = await response.json().catch(() => ({}))
  if (!response.ok || !data.ok) {
    throw new Error(data.error || "Une erreur est survenue.")
  }
  return data
}

const postNote = async ({ root, id, note, participantType }) => {
  const response = await fetch(`/back-office/actions-en-attente/${id}/note`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-CSRF-Token": csrfToken(root),
      "X-Requested-With": "XMLHttpRequest"
    },
    body: JSON.stringify({ note, participant_type: participantType }),
    credentials: "same-origin"
  })

  const data = await response.json().catch(() => ({}))
  if (!response.ok || !data.ok) {
    throw new Error(data.error || "Impossible d’ajouter la note.")
  }
  return data
}

const postUndo = async ({ root, id, noteId, participantType }) => {
  const response = await fetch(`/back-office/actions-en-attente/${id}/undo`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-CSRF-Token": csrfToken(root),
      "X-Requested-With": "XMLHttpRequest"
    },
    body: JSON.stringify({
      participant_type: participantType,
      note_id: noteId || null
    }),
    credentials: "same-origin"
  })

  const data = await response.json().catch(() => ({}))
  if (!response.ok || !data.ok) {
    throw new Error(data.error || "Impossible d’annuler la décision.")
  }
  return data
}

const fetchCardHtml = async (root, item) => {
  const url = cardUrl(root, item)
  if (!url) throw new Error("Impossible de charger la fiche.")

  const response = await fetch(url, {
    headers: {
      Accept: "text/html",
      "X-Requested-With": "XMLHttpRequest"
    },
    credentials: "same-origin"
  })
  if (!response.ok) throw new Error("Impossible de charger la fiche.")
  return response.text()
}

const setBusy = (root, busy) => {
  root.dataset.busy = busy ? "true" : "false"
  root.querySelectorAll("[data-review-action]").forEach((button) => {
    if (button.dataset.reviewAction === "undo") return
    button.disabled = busy
  })
  const note = root.querySelector("[data-review-note]")
  if (note) note.disabled = busy
}

const updateProgress = (root, remaining) => {
  const progress = root.querySelector("[data-review-progress]")
  const label = root.querySelector("[data-review-remaining-label]")
  if (label) label.textContent = remainingLabel(remaining)
  if (progress) progress.hidden = remaining <= 0
}

const showEndScreen = (root, { remaining }) => {
  const empty = root.querySelector("[data-review-empty]")
  const dock = root.querySelector("[data-review-dock]")
  const noteWrap = root.querySelector("[data-review-note-wrap]")
  const slot = root.querySelector("[data-review-card-slot]")
  const title = root.querySelector("[data-review-empty-title]")
  const message = root.querySelector("[data-review-empty-message]")
  const restart = root.querySelector("[data-review-restart]")
  const undo = root.querySelector("[data-review-undo]")
  const decisionActions = root.querySelectorAll(
    '[data-review-action="reject"], [data-review-action="pass"], [data-review-action="approve"]'
  )

  if (slot) slot.innerHTML = ""
  if (noteWrap) noteWrap.hidden = true
  decisionActions.forEach((button) => {
    button.hidden = true
  })
  if (dock) dock.hidden = !(undo && !undo.hidden)
  if (empty) empty.hidden = false

  if (remaining <= 0) {
    if (empty) empty.classList.add("is-all-done")
    if (title) title.textContent = "Bravo 🥳"
    if (message) {
      message.textContent = "Toutes les inscriptions ont été traitées."
    }
    if (restart) restart.hidden = true
    updateProgress(root, 0)
  } else {
    if (empty) empty.classList.remove("is-all-done")
    if (title) title.textContent = "C'est déjà fini 🥹"
    if (message) {
      message.textContent = `Vous avez passé en revue toutes les inscriptions en attente. Il en reste ${remaining} pour lesquelles aucun choix n'a été fait. Recommencez pour les traiter, ou fermez.`
    }
    if (restart) restart.hidden = false
    updateProgress(root, remaining)
  }

  root.querySelector(".backoffice-registration-review__body")?.scrollTo({ top: 0 })
}

const animateOut = (card, direction) =>
  new Promise((resolve) => {
    if (!card) {
      resolve()
      return
    }
    card.classList.remove("is-enter", "is-exit-approve", "is-exit-reject", "is-exit-pass")
    void card.offsetWidth
    card.classList.add(`is-exit-${direction}`)
    window.setTimeout(resolve, ANIMATION_MS)
  })

const restoreBodyScroll = () => {
  const openModal = [...document.querySelectorAll(".backoffice-modal")].some((el) => !el.hidden)
  if (!openModal) document.body.style.overflow = ""
}

const openReviewModal = (root) => {
  root.hidden = false
  document.body.style.overflow = "hidden"
}

const scrollBodyToTop = (root) => {
  root.querySelector(".backoffice-registration-review__body")?.scrollTo({ top: 0 })
}

const isTypingTarget = (target) => {
  if (!(target instanceof Element)) return false
  const tag = target.tagName
  return (
    tag === "TEXTAREA" ||
    tag === "INPUT" ||
    tag === "SELECT" ||
    target.isContentEditable
  )
}

const createSession = (root) => {
  let queue = parseQueue(root)
  let index = 0
  let dirty = false
  let starting = false
  let seenIds = new Set()
  let pendingUndo = null
  let undoTimer = null

  const current = () => queue[index] || null
  const allSeen = () => queue.length > 0 && queue.every((item) => seenIds.has(item.id))
  const modalOpen = () => liveRoot() === root && !root.hidden

  const clearUndo = () => {
    pendingUndo = null
    if (undoTimer) {
      window.clearTimeout(undoTimer)
      undoTimer = null
    }
    const wrap = root.querySelector("[data-review-undo]")
    const fill = root.querySelector("[data-review-undo-fill]")
    const dock = root.querySelector("[data-review-dock]")
    const empty = root.querySelector("[data-review-empty]")
    if (wrap) wrap.hidden = true
    if (fill) {
      fill.classList.remove("is-running")
      void fill.offsetWidth
    }
    if (dock && empty && !empty.hidden) dock.hidden = true
  }

  const armUndo = ({ item, insertAt, noteId }) => {
    clearUndo()
    pendingUndo = { item, insertAt, noteId: noteId || null }
    const wrap = root.querySelector("[data-review-undo]")
    const fill = root.querySelector("[data-review-undo-fill]")
    const dock = root.querySelector("[data-review-dock]")
    if (!wrap) return

    wrap.hidden = false
    if (dock) dock.hidden = false
    if (fill) {
      fill.classList.remove("is-running")
      void fill.offsetWidth
      fill.classList.add("is-running")
    }

    undoTimer = window.setTimeout(() => {
      clearUndo()
    }, UNDO_MS)
  }

  const renderCard = async (item, { animateIn = true } = {}) => {
    const slot = root.querySelector("[data-review-card-slot]")
    const empty = root.querySelector("[data-review-empty]")
    const dock = root.querySelector("[data-review-dock]")
    const noteWrap = root.querySelector("[data-review-note-wrap]")
    if (!slot) return

    const html = await fetchCardHtml(root, item)
    slot.innerHTML = html
    const card = slot.querySelector("[data-review-card]")
    if (empty) empty.hidden = true
    if (dock) dock.hidden = false
    if (noteWrap) noteWrap.hidden = false
    root
      .querySelectorAll(
        '[data-review-action="reject"], [data-review-action="pass"], [data-review-action="approve"]'
      )
      .forEach((button) => {
        button.hidden = false
      })
    if (card && animateIn) card.classList.add("is-enter")
    const note = root.querySelector("[data-review-note]")
    if (note) note.value = ""
    scrollBodyToTop(root)
  }

  const refresh = async ({ animateIn = true } = {}) => {
    if (!current()) {
      showEndScreen(root, { remaining: 0 })
      return
    }
    if (allSeen()) {
      showEndScreen(root, { remaining: queue.length })
      return
    }
    updateProgress(root, queue.length)
    setBusy(root, true)
    try {
      await renderCard(current(), { animateIn })
    } catch (error) {
      showBackofficeFlash(error.message, "alert")
      showEndScreen(root, { remaining: queue.length })
    } finally {
      setBusy(root, false)
    }
  }

  const start = async () => {
    if (starting) return
    if (liveRoot() !== root) return
    starting = true
    queue = parseQueue(root)
    index = 0
    seenIds = new Set()
    clearUndo()
    setBusy(root, true)
    try {
      if (!current()) {
        showEndScreen(root, { remaining: 0 })
      } else {
        updateProgress(root, queue.length)
        await renderCard(current(), { animateIn: false })
      }
      openReviewModal(root)
    } catch (error) {
      showBackofficeFlash(error.message, "alert")
      showEndScreen(root, { remaining: queue.length })
      openReviewModal(root)
    } finally {
      setBusy(root, false)
      starting = false
    }
  }

  const advanceAfterDecision = () => {
    const item = queue[index]
    const insertAt = index
    if (item) seenIds.delete(item.id)
    queue.splice(index, 1)
    if (index >= queue.length) index = 0
    return { item, insertAt }
  }

  const passCurrent = () => {
    const item = queue[index]
    if (!item) return
    seenIds.add(item.id)
    const [removed] = queue.splice(index, 1)
    queue.push(removed)
  }

  const closeModal = () => {
    clearUndo()
    root.hidden = true
    window.setTimeout(() => {
      if (dirty) {
        window.Turbolinks ? Turbolinks.visit(window.location.pathname) : window.location.reload()
      } else {
        restoreBodyScroll()
      }
    }, 0)
  }

  const dispose = () => {
    clearUndo()
    if (!root.hidden) {
      root.hidden = true
      restoreBodyScroll()
    }
  }

  const runAction = async (action) => {
    if (!modalOpen() || root.dataset.busy === "true") return

    if (action === "undo") {
      if (!pendingUndo) return
      const snapshot = pendingUndo
      clearUndo()
      setBusy(root, true)
      try {
        const data = await postUndo({
          root,
          id: snapshot.item.id,
          noteId: snapshot.noteId,
          participantType: root.dataset.participantType || "exhibitor"
        })
        const insertAt = Math.min(snapshot.insertAt, queue.length)
        queue.splice(insertAt, 0, snapshot.item)
        seenIds.delete(snapshot.item.id)
        index = insertAt
        showBackofficeFlash(data.notice || "Décision annulée.", "notice")
        await refresh({ animateIn: true })
      } catch (error) {
        showBackofficeFlash(error.message, "alert")
        setBusy(root, false)
      }
      return
    }

    if (action === "restart") {
      seenIds = new Set()
      index = 0
      clearUndo()
      setBusy(root, true)
      try {
        await refresh({ animateIn: false })
      } finally {
        setBusy(root, false)
      }
      return
    }

    const item = current()
    if (!item) return

    const slot = root.querySelector("[data-review-card-slot]")
    const card = slot?.querySelector("[data-review-card]")
    const note = root.querySelector("[data-review-note]")?.value || ""
    const participantType = root.dataset.participantType || "exhibitor"

    if (action === "pass") {
      setBusy(root, true)
      try {
        const trimmedNote = note.toString().trim()
        if (trimmedNote) {
          await postNote({
            root,
            id: item.id,
            note: trimmedNote,
            participantType
          })
          dirty = true
        }
        await animateOut(card, "pass")
        passCurrent()
        if (allSeen()) {
          showEndScreen(root, { remaining: queue.length })
          setBusy(root, false)
          return
        }
        await refresh({ animateIn: true })
      } catch (error) {
        showBackofficeFlash(error.message, "alert")
        setBusy(root, false)
      }
      return
    }

    if (action !== "approve" && action !== "reject") return

    setBusy(root, true)
    try {
      const data = await postDecision({
        root,
        id: item.id,
        decision: action,
        note,
        participantType
      })
      dirty = true
      await animateOut(card, action === "approve" ? "approve" : "reject")
      const { item: decided, insertAt } = advanceAfterDecision()
      armUndo({ item: decided, insertAt, noteId: data.note_id })
      showBackofficeFlash(data.notice || "Inscription mise à jour.", "notice")
      await refresh({ animateIn: true })
    } catch (error) {
      showBackofficeFlash(error.message, "alert")
      setBusy(root, false)
    }
  }

  root.addEventListener("click", async (event) => {
    if (liveRoot() !== root) return

    const anchor = event.target.closest('a[href^="#"]')
    if (anchor && root.contains(anchor)) {
      event.preventDefault()
      event.stopPropagation()
      const id = anchor.getAttribute("href").slice(1)
      if (!id) return
      const target = root.querySelector(`#${CSS.escape(id)}`)
      const body = root.querySelector(".backoffice-registration-review__body")
      if (!target || !body) return
      const top =
        target.getBoundingClientRect().top - body.getBoundingClientRect().top + body.scrollTop - 12
      body.scrollTo({ top: Math.max(top, 0), behavior: "smooth" })
      return
    }

    if (event.target.closest("[data-close-modal], .backoffice-modal__backdrop")) {
      closeModal()
      return
    }

    const button = event.target.closest("[data-review-action]")
    if (!button) return
    if (button.dataset.reviewAction !== "undo" && root.dataset.busy === "true") return
    await runAction(button.dataset.reviewAction)
  })

  return {
    root,
    start,
    closeModal,
    runAction,
    dispose,
    modalOpen,
    hasPendingUndo: () => Boolean(pendingUndo)
  }
}

const ensureDocumentListeners = () => {
  if (documentListenersBound) return
  documentListenersBound = true

  document.addEventListener(
    "click",
    (event) => {
      const openBtn = event.target.closest('[data-open-modal="backoffice-registration-review-modal"]')
      if (!openBtn) return

      const root = liveRoot()
      if (!root) return

      if (!session || session.root !== root) {
        session?.dispose()
        session = createSession(root)
      }

      event.preventDefault()
      event.stopImmediatePropagation()
      session.start()
    },
    true
  )

  document.addEventListener("keydown", (event) => {
    if (!session || !session.modalOpen()) return
    if (event.defaultPrevented) return

    if (event.key === "Escape") {
      event.preventDefault()
      session.closeModal()
      return
    }

    const typing = isTypingTarget(event.target)
    const key = event.key

    if ((key === "z" || key === "Z") && !event.metaKey && !event.ctrlKey && !event.altKey) {
      if (typing) return
      if (!session.hasPendingUndo()) return
      event.preventDefault()
      session.runAction("undo")
      return
    }

    if (typing) return
    if (event.metaKey || event.ctrlKey || event.altKey) return

    if (key === "ArrowLeft") {
      event.preventDefault()
      session.runAction("reject")
      return
    }

    if (key === "ArrowRight") {
      event.preventDefault()
      session.runAction("approve")
      return
    }

    if (key === " " || key === "Spacebar") {
      event.preventDefault()
      session.runAction("pass")
    }
  })

  document.addEventListener("turbolinks:before-cache", () => {
    if (!session) return
    session.dispose()
    session = null
  })
}

export const initBackofficeRegistrationReview = () => {
  ensureDocumentListeners()
  restoreBodyScroll()

  const root = liveRoot()
  if (!root) {
    if (session) {
      session.dispose()
      session = null
    }
    return
  }

  if (session?.root === root) return

  if (session) session.dispose()
  session = createSession(root)

  const params = new URLSearchParams(window.location.search)
  if (params.get("open_review") === "1") {
    session.start()
    params.delete("open_review")
    const next = `${window.location.pathname}${params.toString() ? `?${params}` : ""}`
    window.history.replaceState({}, "", next)
  }
}

document.addEventListener(
  "backoffice:registration-review-refreshed",
  initBackofficeRegistrationReview
)
